// src/components/PaymentModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { initMercadoPago, Wallet } from '@mercadopago/sdk-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import LoadingSpinner from './ui/LoadingSpinner';
import { ReferralCodeInput } from './ReferralSystem';

const PaymentModal = ({ onPaymentSuccess, onCancel, item, onNavigate }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [dolarRate, setDolarRate] = useState(null);
  const [preferenceId, setPreferenceId] = useState(null);
  const [referralCode, setReferralCode] = useState(null);

  const isLocal = window.location.hostname === 'localhost';
  const price = item?.price || 0;
  const itemName = item?.name || item?.title || 'Producto Místico';

  const handleReferralValidated = useCallback((code) => {
    setReferralCode(code);
  }, []);

  const MERCADO_PAGO_PUBLIC_KEY = process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY;
  const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const EXCHANGE_RATE_API_KEY = process.env.REACT_APP_EXCHANGERATE_API_KEY;

  useEffect(() => {
    if (MERCADO_PAGO_PUBLIC_KEY && !isLocal) {
      initMercadoPago(MERCADO_PAGO_PUBLIC_KEY, { locale: 'es-AR' });
    }
  }, [MERCADO_PAGO_PUBLIC_KEY, isLocal]);

  useEffect(() => {
    if (isLocal || price === 0) return;
    const FALLBACK_RATE = 1300;
    if (!EXCHANGE_RATE_API_KEY || EXCHANGE_RATE_API_KEY === 'TU_API_KEY_DE_EXCHANGERATE') {
      setDolarRate(FALLBACK_RATE);
      return;
    }
    fetch(`https://v6.exchangerate-api.com/v6/${EXCHANGE_RATE_API_KEY}/latest/USD`)
      .then((res) => res.json())
      .then((data) =>
        setDolarRate(
          data.result === 'success' && data.conversion_rates?.ARS
            ? data.conversion_rates.ARS * 1.05
            : FALLBACK_RATE
        )
      )
      .catch(() => setDolarRate(FALLBACK_RATE));
  }, [EXCHANGE_RATE_API_KEY, isLocal, price]);

  const arsPrice = dolarRate ? price * dolarRate : 0;

  const createMercadoPagoPreference = async () => {
    if (!arsPrice) {
      alert('El precio aún no se ha calculado. Intenta de nuevo.');
      return;
    }
    setIsProcessing(true);
    try {
      localStorage.setItem('pendingItem', JSON.stringify(item));
      const returnUrl = window.location.origin + window.location.pathname;

      // Llamar a nuestra API route segura (el token se maneja en el servidor)
      const response = await fetch('/api/create-mp-preference', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemName,
          price: Math.round(arsPrice),
          returnUrl,
        }),
      });

      const data = await response.json();
      if (data.success && data.preferenceId) {
        setPreferenceId(data.preferenceId);
        // Guardar código de referido para registrar comisión post-pago
        if (referralCode) {
          localStorage.setItem('pendingReferral', JSON.stringify({
            code: referralCode,
            itemName,
            totalPaid: Math.round(arsPrice),
          }));
        }
      } else {
        throw new Error(data.error || 'No se pudo crear la preferencia.');
      }
    } catch (error) {
      console.error('Error creando preferencia de MP:', error);
      localStorage.removeItem('pendingItem');
      alert('Hubo un error al iniciar el pago. Si el problema persiste, escribinos a pagos@tarotmistico.lat');
    } finally {
      setIsProcessing(false);
    }
  };

  // Registrar comisión de referido después de un pago exitoso
  const registerReferralCommission = async (refCode, total) => {
    try {
      await fetch('/api/referrals?action=commission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referrerCode: refCode,
          itemName,
          totalPaid: total,
        }),
      });
    } catch (e) {
      console.error('Error registrando comisión:', e);
    }
  };

  // Wrapper de onPaymentSuccess que registra comisión
  const handlePaymentComplete = async () => {
    if (referralCode && arsPrice) {
      await registerReferralCommission(referralCode, Math.round(arsPrice));
    }
    onPaymentSuccess();
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content modal-content--payment" onClick={(e) => e.stopPropagation()}>
        <button onClick={onCancel} className="modal-close-btn" aria-label="Cerrar">✕</button>

        <div className="payment-header">
          <div className="payment-icon">💳</div>
          <h2 className="payment-title font-cinzel">Confirmar Acceso</h2>
        </div>

        {isLocal && (
          <div className="test-mode-banner">
            🧪 <strong>MODO DE PRUEBA LOCAL</strong>
          </div>
        )}

        {isProcessing && !preferenceId ? (
          <LoadingSpinner message="Iniciando pago seguro..." />
        ) : (
          <>
            <div className="payment-item-info">
              <p className="payment-item-name">{itemName}</p>
              <p className="payment-item-price">
                {price === 0 ? (
                  <span className="price-free">✨ Acceso Gratuito</span>
                ) : (
                  <>
                    <span className="price-usd">${price.toFixed(2)} USD</span>
                    {arsPrice > 0 && !isLocal && (
                      <span className="price-ars">≈ ${Math.round(arsPrice).toLocaleString()} ARS</span>
                    )}
                  </>
                )}
              </p>
            </div>

            {/* Campo de código de referido (solo si hay que pagar) */}
            {price > 0 && !isLocal && (
              <ReferralCodeInput onCodeValidated={handleReferralValidated} />
            )}

            {onNavigate && (
              <button
                type="button"
                className="legal-inline-link"
                onClick={() => onNavigate('legal:reembolsos')}
              >
                Términos y Privacidad
              </button>
            )}

            {price === 0 ? (
              <button onClick={onPaymentSuccess} className="main-button primary-button payment-btn-full">
                ✨ Acceder Gratis
              </button>
            ) : isLocal ? (
              <button
                onClick={() => { setIsProcessing(true); setTimeout(handlePaymentComplete, 1500); }}
                className="main-button primary-button payment-btn-full"
              >
                🧪 Simular Pago Exitoso
              </button>
            ) : (
              <div className="payment-options">
                {!preferenceId ? (
                  <button
                    onClick={createMercadoPagoPreference}
                    disabled={!arsPrice}
                    className="mp-button"
                  >
                    {arsPrice ? '🏦 Pagar con Mercado Pago' : 'Calculando precio...'}
                  </button>
                ) : (
                  <Wallet initialization={{ preferenceId }} onReady={() => setIsProcessing(false)} onError={console.error} />
                )}
                <div className="payment-divider"><span>o</span></div>
                {PAYPAL_CLIENT_ID && (
                  <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD' }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', shape: 'pill' }}
                      createOrder={(_, actions) =>
                        actions.order.create({ purchase_units: [{ description: itemName, amount: { value: price.toFixed(2) } }] })
                      }
                      onApprove={async (_, actions) => {
                        setIsProcessing(true);
                        await actions.order.capture();
                        await handlePaymentComplete();
                      }}
                    />
                  </PayPalScriptProvider>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
